<template>
  <div id="app">
    <!-- Masthead — newspaper-style fixed header -->
    <header
      class="masthead"
      :class="{ 'masthead--scrolled': scrolled }"
      aria-label="Main navigation"
    >
      <div class="masthead__rule masthead__rule--top" aria-hidden="true"></div>
      <div class="masthead__inner">
        <a href="#" class="brand" @click.prevent="scrollToTop">
          <span class="brand__mark" aria-hidden="true">⬥</span>
          <span class="brand__word">
            <span class="brand__home">Homerun</span><span class="brand__data">Data</span>
          </span>
          <span class="brand__edition">Pressbox Edition</span>
        </a>

        <button
          class="nav-toggle"
          :class="{ 'nav-toggle--open': menuOpen }"
          aria-label="Toggle navigation"
          @click="menuOpen = !menuOpen"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav class="columns" :class="{ 'columns--open': menuOpen }" aria-label="Sections">
          <template v-if="!isAuthenticated">
            <a href="#metrics" class="column" @click.prevent="navTo('metrics')">
              <span class="column__n">I</span><span class="column__label">Metrics</span>
            </a>
            <a href="#simulation" class="column" @click.prevent="navTo('simulation')">
              <span class="column__n">II</span><span class="column__label">Simulation</span>
            </a>
            <a href="#intelligence" class="column" @click.prevent="navTo('intelligence')">
              <span class="column__n">III</span><span class="column__label">Intelligence</span>
            </a>
            <a href="#dugout" class="column" @click.prevent="navTo('dugout')">
              <span class="column__n">IV</span><span class="column__label">Dugout</span>
            </a>
            <router-link to="/login" class="subscribe" @click="menuOpen = false">
              Subscribe &rarr;
            </router-link>
          </template>
          <template v-else>
            <router-link to="/games" class="column" @click="menuOpen = false">
              <span class="column__n">I</span><span class="column__label">Games</span>
            </router-link>
            <router-link to="/stats" class="column" @click="menuOpen = false">
              <span class="column__n">II</span><span class="column__label">Stats</span>
            </router-link>
            <router-link to="/odds" class="column" @click="menuOpen = false">
              <span class="column__n">III</span><span class="column__label">Odds</span>
            </router-link>
            <router-link
              v-if="user?.is_superuser"
              to="/admin"
              class="column"
              @click="menuOpen = false"
            >
              <span class="column__n">IV</span><span class="column__label">Admin</span>
            </router-link>
            <button class="sign-off" @click="handleLogout">Sign Off</button>
          </template>
        </nav>
      </div>
      <div class="masthead__rule" aria-hidden="true"></div>
    </header>

    <div v-if="menuOpen" class="nav-backdrop" aria-hidden="true" @click="menuOpen = false"></div>

    <main>
      <router-view />
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'

const { isAuthenticated, logout, user } = useAuth()
const route = useRoute()
const router = useRouter()

const scrolled = ref(false)
const menuOpen = ref(false)

const handleLogout = async (): Promise<void> => {
  menuOpen.value = false
  await logout()
}

function navTo(id: string): void {
  menuOpen.value = false
  scrollTo(id)
}

function scrollTo(id: string): void {
  if (route.path !== '/') {
    router.push('/').then(() => {
      nextTick(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
    return
  }
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function scrollToTop(): void {
  menuOpen.value = false
  if (route.path !== '/') {
    router.push('/')
    return
  }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function onScroll(): void {
  scrolled.value = window.scrollY > 40
}

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))
</script>

<style scoped>
.masthead {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--paper);
  transition:
    background 0.25s ease,
    box-shadow 0.25s ease;
}

.masthead--scrolled {
  background: rgba(244, 237, 224, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow:
    0 1px 0 var(--ink),
    0 12px 24px -18px rgba(13, 27, 47, 0.3);
}

.masthead__rule {
  height: 0;
  border-top: var(--rule-thin) solid var(--ink);
}

.masthead__rule--top {
  border-top-width: 3px;
  border-bottom: var(--rule-thin) solid var(--ink);
  height: 5px;
}

.masthead__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.75rem 1.75rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1.5rem;
}

/* ── Brand ─────────────────────────────────────────────────────────── */
.brand {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  text-decoration: none;
  line-height: 1;
  position: relative;
  padding-left: 2rem;
}

.brand__mark {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.1rem;
  color: var(--ox);
}

.brand__word {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  line-height: 1;
}

.brand__home {
  color: var(--ink);
}

.brand__data {
  color: var(--ox);
  font-style: italic;
}

.brand__edition {
  margin-top: 3px;
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  color: var(--ink-faint);
}

/* ── Grid columns (nav links) ───────────────────────────────────────── */
.columns {
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.column {
  display: inline-flex;
  align-items: baseline;
  gap: 0.45rem;
  padding: 0.35rem 0;
  text-decoration: none;
  color: var(--ink);
  font-family: var(--font-ui);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;
  transition: color 0.15s ease;
}

.column::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  height: 2px;
  background: var(--ox);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.column:hover,
.column.router-link-active {
  color: var(--ox);
}

.column:hover::after,
.column.router-link-active::after {
  transform: scaleX(1);
}

.column__n {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 24,
    'wght' 600;
  font-size: 0.8rem;
  font-style: italic;
  color: var(--ox);
  text-transform: none;
  letter-spacing: 0;
}

.column__label {
  /* nothing special */
}

/* ── CTA buttons ────────────────────────────────────────────────────── */
.subscribe,
.sign-off {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--paper);
  background: var(--ink);
  border: none;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;
}

.subscribe:hover,
.sign-off:hover {
  background: var(--ox);
}

/* ── Hamburger ──────────────────────────────────────────────────────── */
.nav-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  z-index: 60;
  justify-self: end;
  grid-column: 3;
}

.nav-toggle span {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--ink);
  transition:
    transform 0.3s,
    opacity 0.3s;
}

.nav-toggle--open span:first-child {
  transform: translateY(7px) rotate(45deg);
}

.nav-toggle--open span:nth-child(2) {
  opacity: 0;
}

.nav-toggle--open span:last-child {
  transform: translateY(-7px) rotate(-45deg);
}

/* ── Mobile ─────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .masthead__inner {
    grid-template-columns: auto auto;
  }

  .nav-toggle {
    display: flex;
  }

  .columns {
    grid-column: 1 / -1;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    padding: 5.5rem 2rem 2rem;
    background: var(--paper);
    border-left: var(--rule-thin) solid var(--ink);
    z-index: 200;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .columns--open {
    transform: translateX(0);
  }

  .column {
    width: 100%;
    padding: 1rem 0;
    border-bottom: var(--rule-thin) solid var(--paper-edge);
    font-size: 0.9rem;
  }

  .subscribe,
  .sign-off {
    margin-top: 1rem;
    width: 100%;
    justify-content: center;
    padding: 0.75rem;
  }
}

.nav-backdrop {
  display: none;
}

@media (max-width: 900px) {
  .nav-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 150;
    background: rgba(13, 27, 47, 0.25);
  }
}
</style>

<style>
@import '@/assets/pressbox.css';
</style>
